<template>
  <div class="content-box">
    <section class="section">
      <h3 class="tit-h3">
        고객 정보
      </h3>
      <dl class="detail">
        <dt>뷰져블 계정</dt>
        <dd>
          {{ data.customer.customerId}}
          <el-tooltip
            :visible="isCopyTooltipVisible"
            content="뷰저블 계정이 복사되었습니다"
            placement="top"
          >
            <el-button
              link
              aria-label="복사하기"
              @click="copyUserId(data.customer.customerId)"
            >
              <font-awesome-icon icon="fa-regular fa-copy" />
            </el-button>
          </el-tooltip>
        </dd>
        <dt>히트맵<br>추천인 코드</dt>
        <dd>
          {{ data.customer.beusRefer ? data.customer.beusRefer : '' }}
        </dd>
        <dt>저니맵<br> 추천인 코드</dt>
        <dd>
          {{ data.customer.baRefer ? data.customer.baRefer : ''}}
        </dd>
        <dt>담당자 정보</dt>
        <dd>
          <div
            class="manager-item"
            v-for="manager in data.customer.manager"
            :key="manager.mail"
          >
            {{ manager.name }} ({{ manager.mail }}{{ manager.phone ? `, ${formatPhonNumber(manager.phone)}` : '' }})
          </div>
        </dd>
        <dt>세금 계산서 <br> 발행 정보</dt>
        <dd>
          <div 
            class="tax-mail-list"
            v-if="data.customer.taxInfo"
          >
            <div
              class="tax-mail-item"
              v-for="mail in data.customer.taxInfo.taxInfoDetail"
              :key="mail"
            >
              {{ mail }}
            </div>
          </div>
          <span v-else>N/A</span>
        </dd>
      </dl>
    </section>
    <section class="section">
      <h3 class="tit-h3">
        요청 상세
      </h3>
      <dl class="detail">
        <dt>요청 유형</dt>
        <dd>{{ getRequestTypeValue(data.detail.requestType) }}</dd>
        <template v-if="data.detail.beusPlanNumber">
          <dt>히트맵 플랜</dt>
          <dd>{{ getBeusPlanLName(data.detail.beusPlanNumber) }}</dd>
        </template>
        <template v-if="data.detail.baPlanNumber">
          <dt>저니맵 플랜</dt>
          <dd>{{ getBaPlanLName(data.detail.baPlanNumber) }}</dd>
        </template>
        <dt>사용 기간</dt>
        <dd>
          {{ data.detail.useStartDate}} 
          <template v-if="data.detail.useEndDate">
            ~ {{ data.detail.useEndDate}}
          </template>
          <template v-if="data.payment.period">
            ({{ data.payment.period }}개월)
          </template>
        </dd>
        <template v-if="data.detail.recurring !== null">
          <dt>자동 갱신 여부</dt>
          <dd>{{ data.detail.recurring ? 'Y' : 'N'}}</dd>
        </template>
      </dl>
    </section>
    <section class="section">
      <h3 class="tit-h3">
        결제 정보
      </h3>
      <dl class="detail">
        <template v-if="data.payment.paymentType === 'free'">
          무상 제공
        </template>
        <template v-else>
          <dt>결제 유형</dt>
          <dd>{{ getPaymentTypeValue(data.payment.paymentType) }}</dd>
          <template v-if="data.payment.paymentCycle && data.payment.paymentCycle > 0">
            <dt>결제 주기</dt>
            <dd>{{ getPaymentCycleTypeValue(data.payment.paymentCycle) }}</dd>
          </template>
          <dt>총 결제 금액</dt>
          <dd v-if="data">
            {{ formatCurrency(data.payment.amount) + ' 원' }} {{ getPaymentAmountDetails }}
          </dd>
          <dt>결제일</dt>
          <dd>{{ calculatePaymentDates(data.payment.paymentCycle, data.payment.paymentDate, data.payment.period) }}</dd>
          <dt>서류 발행</dt>
          <dd>
            <ul 
              v-if="data.payment.docs && data.payment.docs.length > 0"
              class="doc-list"
            >
              <li
                v-for="(doc, index) in data.payment.docs" 
                :key="index"
              >
                {{ doc.actionDate }} / {{ getDocumentTypeValue(doc.docType) }} /
                <template v-if="doc.email === 'system'">
                  <span>별도 시스템(메모 참고)</span> 
                </template>
                <template v-else-if="typeof doc.email === 'string'">
                  <span class="label">{{ doc.email }}</span>
                </template>
                <template v-else>
                  <span 
                    v-for="mail in doc.email" 
                    :key="mail"
                    class="label"
                  >
                    {{ mail }}
                  </span>
                </template>
            </li>
            </ul>
            <p 
              v-else 
              class="desc"
            >
              N/A
            </p>
          </dd>
        </template>
      </dl>
    </section>
    <section class="section">
      <h3 class="tit-h3">
        메모
      </h3>
      <p class="desc">
        {{ data.memo.length === 0 ? '-' : data.memo }}
      </p>
    </section>
  </div>
</template>

<script setup lang='ts'>
import { ref, defineProps, computed } from 'vue';
import { RequestInfo } from '@/types/Request';
import { PaymentCycleTypeKeys} from "@/types/constants";
import { formatCurrency, formatPhonNumber, getBaPlanLName, getBeusPlanLName} from "@/utils";
import { getRequestTypeValue, getPaymentTypeValue, getPaymentCycleTypeValue, getDocumentTypeValue }  from '@/utils/getValueForKey';
import moment from "moment-timezone";

const props = defineProps<{
  data: RequestInfo;
}>();

const isCopyTooltipVisible = ref(false);
const copyUserId = async(text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    isCopyTooltipVisible.value = true;
    setTimeout(() => {
      isCopyTooltipVisible.value = false;
    }, 1000);
  } catch (error) {
    console.error('텍스트 복사 실패:', error);
  }
}

const getPaymentAmountDetails = computed(() => {
  const cycleMap = {
    0: '',
    1: '매월',
    2: '격월마다',
    3: '분기마다',
    4: '4개월마다',
    6: '반기마다',
    12: ''
  }
  const amount = props.data ? props.data.payment.amount : 0
  const paymentCycle = props.data?.payment.paymentCycle ? props.data.payment.paymentCycle : 0
  const period = props.data?.payment.period ? props.data?.payment.period : 1 ;

  if(paymentCycle === 0 || paymentCycle === 12) {
    return '(일시납)'
  }

  const installment = amount / (period / paymentCycle);
  return `(${cycleMap[paymentCycle]} ${formatCurrency(installment)}원 납부)`

});

const calculatePaymentDates = (paymentCycle: PaymentCycleTypeKeys, paymentDate: string, period: number) => {
  const paymentStartDate = moment.tz(paymentDate[0], 'Asia/Seoul');
  if ([null, 0, 12].includes(paymentCycle)) {
    return paymentDate[0];
  }
  if (paymentCycle === 1) {
    if(props.data?.payment.isEndOfMonth) {
      return `${paymentDate[0]} (매월 말일)`;
    } else return `${paymentDate[0]} (매월 ${paymentStartDate.date()}일)`;
  }
  return paymentDate.join(", ");
}
</script>

<style lang="scss" scoped>
.tax-mail-list {
  display: flex;
  gap: 5px;
  .tax-mail-item {
    &:after {
      content: ','
    }
    &:last-child:after {
      content: '';
    }
  }
}

section .detail {
  ul.doc-list > li span.label {
    display: inline-block;
    // padding: 2px 5px;
    // background-color: #f2f2f2;
    // border-radius: 2px;
    & + span.label {
      // margin-left: 5px;
      &:before {
        content: ',';
        padding-right: 5px;
      }
    }
  }
}
</style>